/**
 * CustomBackgroundFontColor Plugin for CKEditor
 *
 * This plugin provides functionality for applying background color and font color to selected text in the CKEditor.
 * It integrates two primary components: 
 * - **CustomFontBackgroundColorEditing**: Handles the editing logic for setting the background color and font color.
 * - **CustomBackgroundFontColorUI**: Manages the user interface for selecting background and font colors.
 *
 * ## Features:
 * - **Font and Background Color Editing**:
 *   - Allows users to set both the font color and background color of selected text.
 *   - Integrates with the CKEditor command system for seamless text color manipulation.
 * - **UI for Color Selection**:
 *   - Adds dropdowns and UI components for the user to pick font and background colors.
 * - **Official Plugin**:
 *   - This plugin is designed to be an official CKEditor plugin, allowing for easy integration into CKEditor-based applications.
 *
 * ## Usage:
 * The plugin works by extending the editor's functionality to allow color selection and application to the selected text. 
 * Both the background and font colors can be changed using the dropdown UI or related commands.
 *
 * Example UI integration:
 * ```javascript
 * editor.ui.componentFactory.add(CKPlugins.CustomBackgroundFontColor, locale => {
 *     // Create dropdown or UI for background and font color selection
 * });
 * ```
 *
 * ## Configuration:
 * The available background and font colors can be configured by setting the `options` property in the plugin's configuration.
 * Example:
 * ```javascript
 * editor.config.set(CKPlugins.CustomBackgroundFontColor, {
 *     fontColorOptions: [...], // List of available font colors
 *     backgroundColorOptions: [...] // List of available background colors
 * });
 * ```
 *
 * ## Implementation Details:
 * - **Font and Background Color Editing**: Uses the `CustomFontBackgroundColorEditing` class to handle applying font and background colors to the selected text.
 * - **UI Integration**: The `CustomBackgroundFontColorUI` class creates the necessary dropdowns and UI components for color selection.
 * - **CKEditor Command Integration**: Uses CKEditor commands to apply the selected colors.
 *
 * ## References:
 * - CKEditor Plugin API: https://ckeditor.com/docs/ckeditor5/latest/api/module_core_plugin-Plugin.html
 * - CKEditor UI Components: https://ckeditor.com/docs/ckeditor5/latest/api/module_ui_componentfactory-ComponentFactory.html
 *
 * @module CustomBackgroundFontColor
 * @exports CustomBackgroundFontColor
 */

import { CustomFontBackgroundColorEditing } from './adapters/fontBackgroundColorEditing';
import { CustomBackgroundFontColorUI } from './adapters/fontBackgroundColorUI';

const Plugin = window.ClassicEditor.Plugin;

export default class CustomBackgroundFontColor extends Plugin {
    static get pluginName() {
        return 'CustomBackgroundFontColor';
    }

    static get requires() {
        return [CustomFontBackgroundColorEditing, CustomBackgroundFontColorUI];
    }

    static get isOfficialPlugin() {
        return true;
    }
}
